import React from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { CopyOutlined, DeleteOutlined, EyeOutlined, PlusOutlined, RobotOutlined, RollbackOutlined } from "@ant-design/icons";
import { ImageClipboardActions, MasterImagePicker, MasterImagePickerField } from "components/pickers/master-image";

const { TextArea } = Input;
const { Text } = Typography;

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Scheduled", value: "scheduled" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
];

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("vi-VN");
  } catch {
    return "-";
  }
};

const PostEditorDrawer = ({
  isMobile = false,
  editorOpen,
  editingPost,
  editorLoading,
  revisionLoading,
  revisions,
  postForm,
  closeEditor,
  savePost,
  checkSlug,
  openPostPreview,
  aiGenerating,
  generatePostHTMLByAI,
    aiFacebookGenerating,
    aiFacebookContent,
    setAIFacebookContent,
    generateFacebookPostByAI,
    copyFacebookPostContent,
        aiImageGenerating,
        aiImagePrompt,
        setAIImagePrompt,
        aiGeneratedImage,
        setAIGeneratedImage,
        generatePostImageByAI,
        applyGeneratedImageAsFeatured,
        appendGeneratedImageToGallery,
  onEditorStatusChange,
  categoryOptions,
  tagOptions,
  restoreRevision,
}) => {
  const currentStatus = Form.useWatch("status", postForm) || "draft";

  const revisionColumns = [
    {
      title: "Thời gian",
      dataIndex: "create_on",
      width: 170,
      render: (v) => formatDate(v),
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 120,
    },
    {
      title: "Revision",
      dataIndex: "revision_no",
      width: 100,
      render: (v) => <Tag>{v || 1}</Tag>,
    },
    {
      title: "Thao tác",
      width: 130,
      render: (_, record) => (
        <Popconfirm title="Khôi phục phiên bản này?" onConfirm={() => restoreRevision(record)}>
          <Button size="small" icon={<RollbackOutlined />}>Khôi phục</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Drawer
      width={isMobile ? "100%" : 980}
      title={editingPost?.id ? "Chỉnh sửa blog" : "Tạo blog"}
      open={editorOpen}
      onClose={closeEditor}
      extra={
        isMobile ? null : (
          <Space>
            <Button onClick={closeEditor}>Đóng</Button>
            <Button type="primary" loading={editorLoading} onClick={savePost}>Lưu</Button>
          </Space>
        )
      }
      footer={
        isMobile ? (
          <Space style={{ width: "100%" }}>
            <Button block onClick={closeEditor}>Đóng</Button>
            <Button block type="primary" loading={editorLoading} onClick={savePost}>Lưu</Button>
          </Space>
        ) : null
      }
    >
      <Form form={postForm} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} lg={16}>
            <Form.Item name="title" label="Tiêu đề" rules={[{ required: true, message: "Nhập tiêu đề" }]}>
              <Input placeholder="Tiêu đề blog" />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item name="slug" label="Đường dẫn tĩnh" rules={[{ required: true, message: "Nhập slug" }]}>
              <Input placeholder="slug-bai-viet" onBlur={(e) => checkSlug(e.target.value)} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="excerpt" label="Tóm tắt">
          <TextArea rows={3} placeholder="Tóm tắt blog" />
        </Form.Item>

        <Space style={{ marginBottom: 12 }}>
          <Button icon={<EyeOutlined />} onClick={() => openPostPreview(editingPost, true)}>
            Xem blog
          </Button>
        </Space>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="status" label="Trạng thái" rules={[{ required: true }]}>
              <Select options={statusOptions} onChange={onEditorStatusChange} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="publish_at"
              label="Lịch đăng"
              rules={
                currentStatus === "scheduled"
                  ? [{ required: true, message: "Nhập thời gian đăng" }]
                  : []
              }
            >
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="unpublish_at" label="Lịch ngừng hiển thị">
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="category_ids" label="Danh mục">
              <Select mode="multiple" options={categoryOptions} allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="tag_ids" label="Tag">
              <Select mode="multiple" options={tagOptions} allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Divider>Media</Divider>

        <MasterImagePickerField
          form={postForm}
          name="featured_image"
          label="Featured image"
          copyLabel="Copy ảnh"
          pasteLabel="Dán ảnh"
          style={{ marginBottom: 12 }}
        />

        <MasterImagePickerField
          form={postForm}
          name="gallery"
          label="Gallery"
          multiple
          maxPhotos={20}
          copyLabel="Copy ảnh đầu"
          pasteLabel="Dán vào gallery"
          style={{ marginBottom: 16 }}
        />

              <Card size="small" title="Tạo hình ảnh bằng AI" style={{ marginBottom: 16 }}>
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                  <Text type="secondary">
                    AI sẽ dùng ngữ cảnh thương hiệu, tiêu đề và tóm tắt để tạo hình ảnh phù hợp cho bài viết.
                  </Text>
                  <TextArea
                    rows={isMobile ? 4 : 3}
                    value={aiImagePrompt}
                    onChange={(e) => setAIImagePrompt(e.target.value)}
                    placeholder="Mô tả thêm cho hình ảnh (ví dụ: phong cách hiện đại, bố cục hero, tông xanh dương)"
                  />
                  <Button
                    icon={<RobotOutlined />}
                    loading={aiImageGenerating}
                    type="primary"
                    onClick={generatePostImageByAI}
                    block={isMobile}
                  >
                    Tạo hình ảnh bằng AI
                  </Button>

                  {aiGeneratedImage?.url ? (
                    <Card size="small" style={{ background: "#fafafa" }}>
                      <Space direction="vertical" size={10} style={{ width: "100%" }}>
                        <img
                          src={aiGeneratedImage.url}
                          alt="Ảnh AI vừa tạo"
                          style={{ width: "100%", borderRadius: 8, border: "1px solid #f0f0f0" }}
                        />
                        <Space wrap>
                          <Button onClick={applyGeneratedImageAsFeatured}>Gán ảnh đại diện</Button>
                          <Button onClick={appendGeneratedImageToGallery}>Thêm vào gallery</Button>
                          <ImageClipboardActions
                            image={aiGeneratedImage}
                            onSetImage={(img) => setAIGeneratedImage?.(img || null)}
                            copyLabel="Copy ảnh AI"
                            pasteLabel="Dán ảnh AI"
                          />
                        </Space>
                      </Space>
                    </Card>
                  ) : null}
                </Space>
              </Card>

              <Card size="small" title="Tạo nội dung Facebook bằng AI" style={{ marginBottom: 16 }}>
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                  <Text type="secondary">
                    AI sẽ tạo caption theo style Facebook, có hook, CTA và hashtag để đăng nhanh.
                  </Text>
                  <Space wrap>
                    <Button
                      icon={<RobotOutlined />}
                      loading={aiFacebookGenerating}
                      type="primary"
                      onClick={generateFacebookPostByAI}
                    >
                      Tạo caption Facebook
                    </Button>
                    <Button icon={<CopyOutlined />} onClick={copyFacebookPostContent}>
                      Copy caption
                    </Button>
                  </Space>
                  <TextArea
                    rows={isMobile ? 8 : 6}
                    value={aiFacebookContent}
                    onChange={(e) => setAIFacebookContent(e.target.value)}
                    placeholder="Caption Facebook do AI tạo sẽ hiển thị ở đây"
                  />
                </Space>
              </Card>

        <Divider>Nội dung</Divider>

        <Form.List name="content_blocks">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
              <Space style={{ justifyContent: "space-between", width: "100%" }}>
                <Text strong>Trình soạn thảo khối</Text>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ type: "paragraph", content: "", image_url: "" })}
                >
                  Thêm khối
                </Button>
              </Space>

              {fields.map(({ key, name, ...restField }, index) => (
                <Card key={key} size="small" title={`Block ${index + 1}`}>
                  <Row gutter={12}>
                    <Col xs={24} md={8}>
                      <Form.Item {...restField} name={[name, "type"]} label="Loại khối">
                        <Select
                          options={[
                            { label: "Paragraph", value: "paragraph" },
                            { label: "Heading", value: "heading" },
                            { label: "Quote", value: "quote" },
                            { label: "Image", value: "image" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={16}>
                      <Form.Item {...restField} name={[name, "content"]} label="Nội dung">
                        <TextArea rows={2} placeholder="Nội dung khối" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col xs={24} md={16}>
                      <Form.Item {...restField} name={[name, "image_url"]} label="URL hình ảnh (nếu có)">
                        <Input placeholder="https://..." />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8} style={{ display: "flex", alignItems: "end" }}>
                      <Button danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                        Xóa khối
                      </Button>
                    </Col>
                  </Row>
                </Card>
              ))}
            </Space>
          )}
        </Form.List>

        <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }}>
          <Text strong>Nội dung HTML</Text>
          <Button icon={<RobotOutlined />} loading={aiGenerating} onClick={generatePostHTMLByAI}>
            Tạo bằng AI
          </Button>
        </Space>

        <Form.Item name="content_html" label="Nội dung HTML">
          <TextArea rows={10} placeholder="Nhập nội dung HTML hoặc text" />
        </Form.Item>

        <Divider>SEO</Divider>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name={["seo", "meta_title"]} label="Meta title">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={["seo", "canonical_url"]} label="Canonical URL">
              <Input placeholder="https://domain.com/slug" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name={["seo", "meta_description"]} label="Meta description">
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item name={["seo", "meta_keywords"]} label="Meta keywords (comma-separated)">
          <Select mode="tags" tokenSeparators={[","]} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name={["seo", "og_title"]} label="OG title">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={["seo", "og_description"]} label="OG description">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <MasterImagePickerField
          form={postForm}
          name={["seo", "og_image"]}
          label="OG image"
          copyLabel="Copy OG ảnh"
          pasteLabel="Dán OG ảnh"
          style={{ marginBottom: 16 }}
        />

        <Form.Item name={["seo", "robots"]} label="Robots">
          <Select
            options={[
              { label: "index,follow", value: "index,follow" },
              { label: "noindex,follow", value: "noindex,follow" },
              { label: "noindex,nofollow", value: "noindex,nofollow" },
            ]}
          />
        </Form.Item>

        {editingPost?.id ? (
          <>
            <Divider>Lịch sử phiên bản</Divider>
            <Table
              rowKey="id"
              loading={revisionLoading}
              columns={revisionColumns}
              dataSource={revisions}
              pagination={false}
              scroll={isMobile ? { x: 700 } : undefined}
            />
          </>
        ) : null}
      </Form>
    </Drawer>
  );
};

export default PostEditorDrawer;
